/**
 * Product Quiz
 *
 * Version:
 *   4.1.0 - 2026/08/24
 */

const SEL_CONTAINER = "[data-product-quiz]";
const SEL_LANDING = "[data-landing]";
const SEL_STEPS = "[data-steps]";
const SEL_STEP = "[data-step]";
const SEL_START = "[data-start]";
const SEL_LOADER = "[data-loader]";
const SEL_RESULTS = "[data-results]";
const SEL_NAV = "[data-nav]";
const SEL_NAV_PREV = "[data-nav-prev]";
const SEL_NAV_NEXT = "[data-nav-next]";
const SEL_NAV_ITEM = "[data-nav-item]";
const SEL_NEXT = "[data-next]";
const SEL_CTA = ".c-product-quiz__cta";
const SEL_OPTION = "[data-option]";
const SEL_WINNERS = "[data-winners-container]";
const SEL_SIMILARS = "[data-similars-container]";
const SEL_SIMILARS_SLIDER = "[data-similars-slider]";
const SEL_ATC = ".hdt-product-card-btn--atc";

const VAR_STEP_PREFIX = "quiz_step_";
const VAR_PARAM_STEP = "step";
const VAR_PARAM_OPTIONS = "options";
const LEGACY_PARAM_STEP = "quiz_step";
const LEGACY_PARAM_OPTIONS = "quiz_options";
const OPTIONS_PAYLOAD_VERSION = "v1";
const ATTR_STEP_HANDLE = "data-step-handle";
const ATTR_STEP_INDEX = "data-step-index";
const ATTR_OPTION_INDEX = "data-option-index";
const ATTR_STEP_QUESTION = "data-question";
const ATTR_QUIZ_TITLE = "data-product-quiz";
const ATTR_HANDLE = "data-quiz-product-handle";
const CLASS_ACTIVE = "is-active";
const CLASS_HIDDEN = "is-hidden";
const CLASS_DISABLED = "is-disabled";
const STYLE_PROGRESS = "--var-quiz-progress";
const QUIZ_CARD_SECTION = "sn-product-quiz-card";
const NAV_VISIBLE_MOBILE = 4;
const NAV_VISIBLE_DESKTOP = 6;
const NAV_DESKTOP_MIN_WIDTH = 768;
const RESULTS_LOADER_MS = 2000;
const FETCH_TIMEOUT_MS = 8000;
const SEL_QUIZ_CARD = ".c-product-quiz__product";
const SEL_CANONICAL_CARD = "hdt-card-product";

function extractQuizCardHtml(html) {
  if (!html) return null;

  const trimmed = html.trim();

  if (!trimmed.includes("shopify-section")) {
    return isValidQuizCardHtml(trimmed) ? trimmed : null;
  }

  const doc = new DOMParser().parseFromString(trimmed, "text/html");
  const slide =
    doc.querySelector(`${SEL_QUIZ_CARD}[${ATTR_HANDLE}]`) ||
    doc.querySelector(`[${ATTR_HANDLE}]`);

  if (slide) return slide.outerHTML;

  const section = doc.querySelector(".shopify-section");

  if (section?.innerHTML.trim()) {
    const innerSlide =
      section.querySelector(`${SEL_QUIZ_CARD}[${ATTR_HANDLE}]`) ||
      section.querySelector(`[${ATTR_HANDLE}]`);

    return innerSlide?.outerHTML || null;
  }

  return null;
}

function isValidQuizCardHtml(html) {
  if (!html || !html.includes(ATTR_HANDLE)) return false;

  const doc = new DOMParser().parseFromString(html, "text/html");

  return Boolean(
    doc.querySelector(`${SEL_QUIZ_CARD}[${ATTR_HANDLE}] ${SEL_CANONICAL_CARD}`) ||
      doc.querySelector(`[${ATTR_HANDLE}] ${SEL_CANONICAL_CARD}`)
  );
}

function encodeBase64Url(value) {
  const bytes = new TextEncoder().encode(value);
  let binary = "";

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function decodeBase64Url(value) {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));

  return new TextDecoder().decode(bytes);
}

function buildOptionsCompact(options) {
  const grouped = new Map();

  options.forEach((opt) => {
    if (!opt.checked) return;

    const stepIndex = opt.getAttribute(ATTR_STEP_INDEX);
    const optionIndex = opt.getAttribute(ATTR_OPTION_INDEX);

    if (!stepIndex || !optionIndex) return;

    if (!grouped.has(stepIndex)) {
      grouped.set(stepIndex, []);
    }

    grouped.get(stepIndex).push(optionIndex);
  });

  const segments = Array.from(grouped.entries())
    .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
    .map(([stepIndex, optionIndexes]) => {
      const sortedOptions = optionIndexes.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
      return `${stepIndex}-${sortedOptions.join(".")}`;
    });

  if (!segments.length) return "";

  return `${OPTIONS_PAYLOAD_VERSION}|${segments.join(",")}`;
}

function encodeOptionsParam(options) {
  const compact = buildOptionsCompact(options);

  if (!compact) return "";

  return encodeBase64Url(compact);
}

function decodeOptionsParam(encoded) {
  if (!encoded) return "";

  try {
    const compact = decodeBase64Url(decodeURIComponent(encoded));

    if (!compact.startsWith(`${OPTIONS_PAYLOAD_VERSION}|`)) return "";

    return compact;
  } catch (error) {
    return "";
  }
}

function parseLegacyOptionsParam(value) {
  const grouped = new Map();

  decodeURIComponent(value)
    .split(",")
    .forEach((token) => {
      const match = token.trim().match(/^step_(\d+)_opt_(\d+)$/);

      if (!match) return;

      const step = match[1];
      const option = match[2];

      if (!grouped.has(step)) {
        grouped.set(step, []);
      }

      grouped.get(step).push(option);
    });

  const segments = Array.from(grouped.entries())
    .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
    .map(([step, optionIndexes]) => {
      const sortedOptions = optionIndexes.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
      return `${step}-${sortedOptions.join(".")}`;
    });

  if (!segments.length) return "";

  return `${OPTIONS_PAYLOAD_VERSION}|${segments.join(",")}`;
}

function applyOptionsFromCompact(compact, options) {
  if (!compact) return;

  const payload = compact.replace(`${OPTIONS_PAYLOAD_VERSION}|`, "");

  if (!payload) return;

  const selections = new Map();

  payload.split(",").forEach((segment) => {
    const dashIndex = segment.indexOf("-");

    if (dashIndex === -1) return;

    const stepIndex = segment.slice(0, dashIndex);
    const optionPart = segment.slice(dashIndex + 1);

    selections.set(stepIndex, optionPart.split("."));
  });

  options.forEach((opt) => {
    opt.checked = false;
  });

  options.forEach((opt) => {
    const stepIndex = opt.getAttribute(ATTR_STEP_INDEX);
    const optionIndex = opt.getAttribute(ATTR_OPTION_INDEX);

    if (!stepIndex || !optionIndex) return;

    const selectedOptions = selections.get(stepIndex);

    if (selectedOptions?.includes(optionIndex)) {
      opt.checked = true;
    }
  });
}

class ProductQuiz {
  constructor(container) {
    this.container = container;
    this.landing = container.querySelector(SEL_LANDING);
    this.steps = container.querySelector(SEL_STEPS);
    this.stepItems = Array.from(container.querySelectorAll(SEL_STEP));
    this.startButton = container.querySelector(SEL_START);
    this.loader = container.querySelector(SEL_LOADER);
    this.results = container.querySelector(SEL_RESULTS);
    this.nav = container.querySelector(SEL_NAV);
    this.navPrevButton = container.querySelector(SEL_NAV_PREV);
    this.navNextButton = container.querySelector(SEL_NAV_NEXT);
    this.navItems = Array.from(container.querySelectorAll(SEL_NAV_ITEM));
    this.nextButtons = Array.from(container.querySelectorAll(SEL_NEXT));
    this.options = Array.from(container.querySelectorAll(SEL_OPTION));
    this.winners = Array.from(container.querySelectorAll(SEL_WINNERS));
    this.similars = Array.from(container.querySelectorAll(SEL_SIMILARS));
  }

  init() {
    this.startButton?.addEventListener("click", (e) => this.onStartClick(e));
    this.navPrevButton?.addEventListener("click", (e) => this.onPrevClick(e));
    this.navNextButton?.addEventListener("click", (e) => this.onNextClick(e));
    this.nextButtons.forEach((el) => el.addEventListener("click", (e) => this.onNextClick(e)));
    this.navItems.forEach((el) => el.addEventListener("click", (e) => this.onNavClick(e)));
    this.options.forEach((el) => el.addEventListener("change", (e) => this.onOptionSelected(e)));

    this.migrateLegacyUrlParams();

    if (this.hasStepParams) {
      this.hideContainers();
      this.showStepContainers();
      this.clearActiveElements();
      this.setActiveElements(this.activeStep);
      this.clearHiddenNavs();
      this.updateSelectedOptions();
      this.updateVisibleNavs();
      this.refreshNavigationState();
    }

    this.onNavResize = () => {
      if (!this.nav?.classList.contains(CLASS_ACTIVE)) return;

      this.clearHiddenNavs();
      this.updateVisibleNavs();
    };

    window.addEventListener("resize", this.onNavResize, { passive: true });
  }

  getVisibleNavCount() {
    return window.matchMedia(`(min-width: ${NAV_DESKTOP_MIN_WIDTH}px)`).matches
      ? NAV_VISIBLE_DESKTOP
      : NAV_VISIBLE_MOBILE;
  }

  get hasStepParams() {
    return new URL(window.location).searchParams.has(VAR_PARAM_STEP);
  }

  get activeStep() {
    const value = new URL(window.location).searchParams.get(VAR_PARAM_STEP);
    return parseInt(value, 10) || 1;
  }

  get nextStep() {
    const nextIndex = this.activeStep + 1;
    return nextIndex <= this.stepItems.length ? nextIndex : this.stepItems.length;
  }

  get prevStep() {
    const prevIndex = this.activeStep - 1;
    return prevIndex > 0 ? prevIndex : 1;
  }

  getAttributeStep(element) {
    if (!element) return;

    const handle = element.getAttribute(ATTR_STEP_HANDLE);
    const handleSplit = handle.split("_");
    return handleSplit?.length ? parseInt(handleSplit[handleSplit.length - 1], 10) : 1;
  }

  hasSelected(activeStep = this.activeStep) {
    const inputs = Array.from(
      this.container.querySelectorAll(`input[data-step-handle="${VAR_STEP_PREFIX}${activeStep}"]`)
    );

    return inputs.some((input) => input.checked);
  }

  hideContainers() {
    this.landing?.classList.remove(CLASS_ACTIVE);
    this.steps?.classList.remove(CLASS_ACTIVE);
    this.loader?.classList.remove(CLASS_ACTIVE);
    this.results?.classList.remove(CLASS_ACTIVE);
    this.nav?.classList.remove(CLASS_ACTIVE);
  }

  showStepContainers() {
    this.steps?.classList.add(CLASS_ACTIVE);
    this.nav?.classList.add(CLASS_ACTIVE);
  }

  setActiveElements(stepIndex = 1) {
    const stepHandle = `${VAR_STEP_PREFIX}${stepIndex}`;
    const activeStep = this.stepItems.find((el) => el.getAttribute(ATTR_STEP_HANDLE) === stepHandle);
    activeStep?.classList.add(CLASS_ACTIVE);

    const activeNav = this.navItems.find((el) => el.getAttribute(ATTR_STEP_HANDLE) === stepHandle);
    activeNav?.classList.add(CLASS_ACTIVE);
  }

  clearActiveElements() {
    this.stepItems.forEach((el) => el.classList.remove(CLASS_ACTIVE));
    this.navItems.forEach((el) => el.classList.remove(CLASS_ACTIVE));
  }

  setStepParam(stepIndex = 1) {
    const currentUrl = new URL(window.location);
    currentUrl.searchParams.set(VAR_PARAM_STEP, stepIndex);
    currentUrl.searchParams.delete(LEGACY_PARAM_STEP);
    window.history.pushState({}, "", currentUrl.toString());
  }

  syncResultsFromSelections() {
    const selectionsProducts = [];
    const selectionsProductsTitles = [];

    this.options.forEach((opt) => {
      if (!opt.checked) return;

      const currentValues = (opt.getAttribute("data-products-handles") || "").split(",");
      const currentValuesTitles = (opt.getAttribute("data-products-titles") || "").split(",");

      currentValues.forEach((val, i) => {
        if (val !== "") {
          selectionsProducts.push(val);
          selectionsProductsTitles.push(currentValuesTitles[i]);
        }
      });
    });

    this.saveResults(selectionsProducts, selectionsProductsTitles);
  }

  setOptionParam() {
    this.syncResultsFromSelections();

    const currentUrl = new URL(window.location);
    const encoded = encodeOptionsParam(this.options);

    if (encoded) {
      currentUrl.searchParams.set(VAR_PARAM_OPTIONS, encoded);
    } else {
      currentUrl.searchParams.delete(VAR_PARAM_OPTIONS);
    }

    currentUrl.searchParams.delete(LEGACY_PARAM_OPTIONS);
    window.history.pushState({}, "", currentUrl.toString());
  }

  updateSelectedOptions() {
    const params = new URL(window.location).searchParams;
    const encoded = params.get(VAR_PARAM_OPTIONS);

    if (encoded) {
      applyOptionsFromCompact(decodeOptionsParam(encoded), this.options);
      this.syncResultsFromSelections();
      return;
    }

    const legacy = params.get(LEGACY_PARAM_OPTIONS);

    if (!legacy) return;

    applyOptionsFromCompact(parseLegacyOptionsParam(legacy), this.options);
    this.syncResultsFromSelections();
  }

  migrateLegacyUrlParams() {
    const currentUrl = new URL(window.location);
    let migrated = false;

    if (!currentUrl.searchParams.has(VAR_PARAM_STEP) && currentUrl.searchParams.has(LEGACY_PARAM_STEP)) {
      currentUrl.searchParams.set(VAR_PARAM_STEP, currentUrl.searchParams.get(LEGACY_PARAM_STEP));
      currentUrl.searchParams.delete(LEGACY_PARAM_STEP);
      migrated = true;
    }

    if (!currentUrl.searchParams.has(VAR_PARAM_OPTIONS) && currentUrl.searchParams.has(LEGACY_PARAM_OPTIONS)) {
      const compact = parseLegacyOptionsParam(currentUrl.searchParams.get(LEGACY_PARAM_OPTIONS));

      if (compact) {
        currentUrl.searchParams.set(VAR_PARAM_OPTIONS, encodeBase64Url(compact));
      }

      currentUrl.searchParams.delete(LEGACY_PARAM_OPTIONS);
      migrated = true;
    }

    if (migrated) {
      window.history.replaceState({}, "", currentUrl.toString());
    }
  }

  removeUrlParam() {
    const currentUrl = new URL(window.location);
    currentUrl.searchParams.delete(VAR_PARAM_STEP);
    currentUrl.searchParams.delete(VAR_PARAM_OPTIONS);
    currentUrl.searchParams.delete(LEGACY_PARAM_STEP);
    currentUrl.searchParams.delete(LEGACY_PARAM_OPTIONS);
    window.history.pushState({}, "", currentUrl.toString());
  }

  clearHiddenNavs() {
    this.navItems.forEach((el) => el.parentElement.classList.remove(CLASS_HIDDEN));
  }

  updateVisibleNavs() {
    const activeItem = this.activeStep;
    const navLen = this.navItems.length;
    const visibleItems = this.getVisibleNavCount();

    let firstItem = activeItem > visibleItems - 1 ? activeItem - (visibleItems - 2) : 1;
    const estimateLastItem = firstItem + (visibleItems - 1);
    const finalItem = navLen >= estimateLastItem ? estimateLastItem : navLen;
    firstItem = finalItem === navLen ? finalItem - (visibleItems - 1) : firstItem;

    this.navItems.forEach((item, index) => {
      const currentItem = index + 1;

      if (currentItem < firstItem || currentItem > finalItem) {
        item.parentElement.classList.add(CLASS_HIDDEN);
      }
    });
  }

  updateProgressBar() {
    const itemsLen = this.stepItems.length;
    const progress = (parseFloat(this.activeStep / itemsLen)) * 100;
    this.nav?.style.setProperty(STYLE_PROGRESS, `${progress}%`);
  }

  updateArrowState() {
    this.navPrevButton.classList.toggle(CLASS_DISABLED, this.activeStep <= 1);
    this.navNextButton.classList.toggle(CLASS_DISABLED, !this.hasSelected());
  }

  updateNavItemState() {
    this.navItems.forEach((item) => {
      const step = this.getAttributeStep(item);

      if (step === this.activeStep || this.hasSelected(step)) {
        item.classList.remove(CLASS_DISABLED);
      }

      if (step === this.nextStep) {
        item.classList.toggle(CLASS_DISABLED, !this.hasSelected());
      }
    });
  }

  updateNextState() {
    const currentStepEl = this.stepItems.find(
      (el) => el.getAttribute(ATTR_STEP_HANDLE) === `${VAR_STEP_PREFIX}${this.activeStep}`
    );

    if (!currentStepEl) return;

    const button = currentStepEl.querySelector(`${SEL_NEXT}, ${SEL_CTA}[data-next]`);

    if (this.hasSelected()) {
      button?.classList.remove(CLASS_DISABLED);
      button?.removeAttribute("disabled");
      button?.removeAttribute("aria-disabled");
    } else {
      button?.classList.add(CLASS_DISABLED);
      button?.setAttribute("disabled", "");
      button?.setAttribute("aria-disabled", "true");
    }
  }

  refreshNavigationState() {
    this.updateProgressBar();
    this.updateArrowState();
    this.updateNavItemState();
    this.updateNextState();
  }

  showResults() {
    if (!this.loader || !this.results) {
      console.error("[ProductQuiz] Missing loader or results element");
      return;
    }

    this._resultsRevealed = false;
    this.loader.classList.add(CLASS_ACTIVE);

    this.generateProductResults()
      .then(() => {
        setTimeout(() => this.revealResults(), RESULTS_LOADER_MS);
      })
      .catch((error) => {
        console.error("[ProductQuiz] Failed to generate results:", error);
        setTimeout(() => this.revealResults(), RESULTS_LOADER_MS);
      });
  }

  getStoreRoot() {
    return window.Shopify?.routes?.root || "/";
  }

  buildSectionCardUrl(handle) {
    const root = this.getStoreRoot();
    return `${root}products/${encodeURIComponent(handle)}?sections=${QUIZ_CARD_SECTION}`;
  }

  revealResults() {
    if (this._resultsRevealed) return;
    this._resultsRevealed = true;

    this.loader?.classList.remove(CLASS_ACTIVE);
    this.results?.classList.add(CLASS_ACTIVE);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.initQuizResults();
      });
    });

    this.gtmQuizTracking("quiz_completed");

    Array.from(this.container.querySelectorAll(SEL_ATC)).forEach((btn) => {
      btn.addEventListener("click", () => this.gtmQuizTracking("quiz_converted"));
    });
  }

  determineResults(arr) {
    return arr.reduce((value, value2) => {
      value[value2] ? ++value[value2] : (value[value2] = 1);
      return value;
    }, {});
  }

  cleanTitleString(str) {
    return str.replace(/\+.*?"/g, '"');
  }

  saveResults(arr1, arr2) {
    const results = this.determineResults(arr1);
    const resultsTitles = this.determineResults(arr2);
    sessionStorage.setItem("quizHandles", JSON.stringify(results));
    sessionStorage.setItem("quizTitles", this.cleanTitleString(JSON.stringify(resultsTitles)));
  }

  triggerKlaviyoTracking(winnersArr, winnersHandlesArr) {
    const quizData = {};
    const quizTitle = this.container.getAttribute(ATTR_QUIZ_TITLE) || "Quiz";

    this.stepItems.forEach((step) => {
      if (!step.hasAttribute(ATTR_STEP_QUESTION)) return;

      const question = step.getAttribute(ATTR_STEP_QUESTION);
      const selectedOptions = step.querySelectorAll("input:checked");
      const answers = [];

      selectedOptions.forEach((input) => {
        const answerText = input.nextElementSibling
          ?.querySelector(".c-product-quiz__option-name")
          ?.textContent.trim();

        if (!answerText) return;

        if (input.type === "checkbox") {
          answers.push(answerText);
        } else if (input.type === "radio") {
          answers.length = 0;
          answers.push(answerText);
        }
      });

      quizData[question] = answers.length > 1 ? answers : answers[0];
    });

    const productGenerated = this.container.querySelectorAll(`[${ATTR_HANDLE}]`);
    let image1 = "";
    let image2 = "";

    productGenerated.forEach((prod) => {
      if (prod.getAttribute(ATTR_HANDLE) === winnersHandlesArr[0]) {
        image1 = prod.getAttribute("data-quiz-image-url");
      }
      if (prod.getAttribute(ATTR_HANDLE) === winnersHandlesArr[1]) {
        image2 = prod.getAttribute("data-quiz-image-url");
      }
    });

    const learnq = window._learnq || [];

    const quizTracker = { quiz_type: quizTitle, ...quizData };

    if (image1 !== "") {
      quizTracker.prod1_title = winnersArr[0];
      quizTracker.prod1_handle = winnersHandlesArr[0];
      quizTracker.prod1_image = image1;
    }

    if (image2 !== "") {
      quizTracker.prod2_title = winnersArr[1];
      quizTracker.prod2_handle = winnersHandlesArr[1];
      quizTracker.prod2_image = image2;
    }

    learnq.push(["track", "quiz", quizTracker]);
  }

  async generateProductResults() {
    this.syncResultsFromSelections();
    this.clearResultContainers();

    let winnerObj = {};
    let winnerObjTitles = {};

    try {
      winnerObj = JSON.parse(sessionStorage.getItem("quizHandles") || "{}");
      winnerObjTitles = JSON.parse(sessionStorage.getItem("quizTitles") || "{}");
    } catch (error) {
      winnerObj = {};
      winnerObjTitles = {};
    }

    const winners = this.sortObjectEntries(winnerObj).slice(0, 2);
    const winnersTitles = this.sortObjectEntries(winnerObjTitles).slice(0, 2);

    await this.fetchProducts(winners, this.winners, winnersTitles);

    const similars = this.sortObjectEntries(winnerObj).slice(2);
    const similarsTitles = this.sortObjectEntries(winnerObjTitles).slice(2);
    await this.fetchProducts(similars, this.similars, similarsTitles);

    setTimeout(() => {
      this.triggerKlaviyoTracking(winnersTitles, winners);
    }, 3000);
  }

  refreshResultContainers() {
    this.winners = Array.from(this.container.querySelectorAll(SEL_WINNERS));
    this.similars = Array.from(this.container.querySelectorAll(SEL_SIMILARS));
  }

  clearResultContainers() {
    this.refreshResultContainers();
    this.winners.forEach((container) => {
      container.innerHTML = "";
    });
    this.similars.forEach((container) => {
      container.innerHTML = "";
    });
  }

  fetchWithTimeout(url, timeoutMs = FETCH_TIMEOUT_MS) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    return fetch(url, { signal: controller.signal }).finally(() => {
      clearTimeout(timeoutId);
    });
  }

  initQuizResults() {
    this.initResultsSliders();
    this.initResultProductCards();
  }

  initResultsSliders() {
    this.container.querySelectorAll(SEL_SIMILARS_SLIDER).forEach((sliderEl) => {
      try {
        this.initResultsSlider(sliderEl);
      } catch (error) {
        console.warn("[ProductQuiz] Slider init failed:", error);
      }
    });
  }

  /**
   * Featured-collection sliders init on connect with slides already in the DOM.
   * Quiz cards are injected later into an empty slider, so remount to re-run
   * connectedCallback after slides exist (reInit alone is a no-op without apiS).
   */
  remountResultsSlider(sliderEl) {
    const parent = sliderEl.parentNode;

    if (!parent) return sliderEl;

    const next = sliderEl.nextSibling;
    parent.removeChild(sliderEl);
    parent.insertBefore(sliderEl, next);

    return sliderEl;
  }

  initResultsSlider(sliderEl) {
    const hasSlides = sliderEl.querySelectorAll(".hdt-slider__slide").length > 0;

    if (!hasSlides) return;

    if (!sliderEl.apiS) {
      sliderEl = this.remountResultsSlider(sliderEl);
    }

    if (!sliderEl.apiS && typeof sliderEl.connectedCallback === "function") {
      sliderEl.connectedCallback();
    } else if (typeof sliderEl.reInit === "function") {
      sliderEl.reInit();
    } else if (sliderEl.apiS?.reInit) {
      sliderEl.apiS.reInit();
    }

    if (typeof sliderEl.reveal === "function") {
      sliderEl.reveal();
    }

    sliderEl.querySelector("hdt-slider-dots")?.removeAttribute("hidden");

    requestAnimationFrame(() => {
      if (typeof sliderEl.reInit === "function") {
        sliderEl.reInit();
      } else if (sliderEl.apiS?.reInit) {
        sliderEl.apiS.reInit();
      }

      window.dispatchEvent(new Event("resize"));
    });
  }

  initResultProductCards() {
    this.container.querySelectorAll(`${SEL_WINNERS}, ${SEL_SIMILARS}`).forEach((container) => {
      container.dispatchEvent(new CustomEvent("products:update", { bubbles: true }));
    });

    document.dispatchEvent(new CustomEvent("currency:update"));
  }

  sortObjectEntries(obj) {
    return Object.entries(obj)
      .sort((a, b) => b[1] - a[1])
      .map((el) => el[0]);
  }

  async fetchSectionCardHtml(handle) {
    if (!handle) return null;

    try {
      const sectionResponse = await this.fetchWithTimeout(this.buildSectionCardUrl(handle));

      if (!sectionResponse.ok) {
        console.warn("[ProductQuiz] Section card fetch HTTP error:", handle, sectionResponse.status);
        return null;
      }

      const sectionData = await sectionResponse.json();
      const sectionHtml = extractQuizCardHtml(sectionData[QUIZ_CARD_SECTION]);

      if (sectionHtml && isValidQuizCardHtml(sectionHtml)) {
        return sectionHtml;
      }

      console.warn("[ProductQuiz] Section card missing product markup:", handle, sectionData[QUIZ_CARD_SECTION]?.slice(0, 200));
    } catch (error) {
      console.warn("[ProductQuiz] Section card fetch failed:", handle, error);
    }

    return null;
  }

  async fetchQuizCardHtml(handle) {
    if (!handle) return null;

    try {
      const searchResponse = await this.fetchWithTimeout(
        `${this.getStoreRoot()}search?view=quiz-result&type=product&q=${encodeURIComponent(handle)}`
      );
      const searchHtml = extractQuizCardHtml((await searchResponse.text()).trim());

      if (searchHtml) return searchHtml;
    } catch (error) {
      console.warn("[ProductQuiz] Quiz card fetch failed:", handle, error);
    }

    return null;
  }

  async fetchProductHtml(handle, title) {
    const quizHtml = await this.fetchQuizCardHtml(handle);

    if (quizHtml) return quizHtml;

    const sectionHtml = await this.fetchSectionCardHtml(handle);

    if (sectionHtml) return sectionHtml;

    if (!title || title === handle) return null;

    return this.fetchQuizCardHtml(title);
  }

  async fetchProducts(handlesArr, resultContainers, titlesArr) {
    await Promise.all(
      resultContainers.map(async (container) => {
        const htmlParts = await Promise.all(
          handlesArr.map((handle, i) => this.fetchProductHtml(handle, titlesArr[i]))
        );

        htmlParts.forEach((html) => {
          const slideHtml = extractQuizCardHtml(html);

          if (slideHtml) {
            container.insertAdjacentHTML("beforeend", slideHtml);
          }
        });
      })
    );
  }

  gtmQuizTracking(event) {
    const quizTitle = this.container.getAttribute(ATTR_QUIZ_TITLE) || "Quiz";

    if (typeof Shopify !== "undefined" && Shopify.analytics?.publish) {
      Shopify.analytics.publish(event, { quiz: quizTitle });
    }

    if (typeof window.clarity === "function") {
      window.clarity("event", event);
    }
  }

  onStartClick() {
    this.gtmQuizTracking("quiz_started");
    this.hideContainers();
    this.showStepContainers();
    this.clearActiveElements();
    this.setStepParam();
    this.setActiveElements();
    this.clearHiddenNavs();
    this.updateVisibleNavs();
    this.refreshNavigationState();
  }

  onOptionSelected() {
    this.setOptionParam();
    this.updateNextState();
    this.updateArrowState();
    this.updateNavItemState();
  }

  onNextClick() {
    if (this.activeStep === this.stepItems.length) {
      this.finishQuiz();
      return;
    }

    this.clearActiveElements();
    this.setActiveElements(this.nextStep);
    this.setStepParam(this.nextStep);
    this.clearHiddenNavs();
    this.updateVisibleNavs();
    this.refreshNavigationState();
  }

  onPrevClick() {
    this.clearActiveElements();
    this.setActiveElements(this.prevStep);
    this.setStepParam(this.prevStep);
    this.clearHiddenNavs();
    this.updateVisibleNavs();
    this.refreshNavigationState();
  }

  onNavClick(e) {
    const targetEl = e.currentTarget;
    const index = this.getAttributeStep(targetEl);

    if (!index) return;

    this.clearActiveElements();
    this.setActiveElements(index);
    this.setStepParam(index);
    this.clearHiddenNavs();
    this.updateVisibleNavs();
    this.refreshNavigationState();
  }

  finishQuiz() {
    this.hideContainers();
    this.removeUrlParam();
    this.showResults();
  }
}

document.querySelectorAll(SEL_CONTAINER).forEach((container) => {
  const quiz = new ProductQuiz(container);
  quiz.init();
});
